const normalizeRef = ref => String(ref || '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

export function footnotePopoverMaxHeight(top, flip, viewportHeight) {
  const space = flip ? top : viewportHeight - top;
  return Math.min(480, Math.max(0, space - 16));
}

// A mirror returns the containing section. Pick the cited verse/comment even
// when that section has many paragraphs, instead of displaying its opening.
export function sourcePreviewSegments(text, ref) {
  const segments = text?.segments || [];
  const requested = normalizeRef(ref);
  const exact = segments.find(segment => normalizeRef(segment.ref) === requested);
  if (exact) return [exact];
  const range = requested.match(/^(.*?)(\d+)-(\d+)$/);
  if (range) {
    const matching = segments.filter(segment => {
      const point = normalizeRef(segment.ref).match(/^(.*?)(\d+)$/);
      return point && point[1] === range[1] && Number(point[2]) >= Number(range[2]) && Number(point[2]) <= Number(range[3]);
    });
    if (matching.length) return matching;
  }
  const children = segments.filter(segment => normalizeRef(segment.ref).startsWith(requested + ':'));
  if (children.length) return children.slice(0, 3);
  const focus = segments.find(segment => normalizeRef(segment.ref) === normalizeRef(text?.focusRef));
  return focus ? [focus] : segments.slice(0, 2);
}

const previews = new Map();
const queue = [];
let active = 0;
function drain() {
  while (active < 4 && queue.length) {
    const task = queue.shift();
    active++;
    task.run().then(task.resolve, task.reject).finally(() => { active--; drain(); });
  }
}

// Expanded resources share bounded requests so a long commentary panel
// cannot flood the service or repeatedly fetch the same exact passage.
export function loadSourcePreview(ref) {
  if (previews.has(ref)) return previews.get(ref);
  const pending = new Promise((resolve, reject) => {
    queue.push({ resolve, reject, run: async () => {
      const response = await fetch(`/bff/text/${encodeURIComponent(ref.replace(/ /g, '_'))}?build=20261014-source-previews`, { signal: AbortSignal.timeout(12000) });
      if (!response.ok) throw new Error('Source preview unavailable');
      const text = await response.json();
      if (!text?.segments?.some(segment => segment.he || segment.en)) throw new Error('Source preview unavailable');
      return text;
    } });
    drain();
  });
  previews.set(ref, pending);
  pending.catch(() => { if (previews.get(ref) === pending) previews.delete(ref); });
  if (previews.size > 96) previews.delete(previews.keys().next().value);
  return pending;
}
